// Loading and decoding the static data contract (docs/DESIGN.md → "Web data contract").
// Columnar JSON is turned into typed arrays once; everything is cached per URL.

import type { DatePoint } from "./time";
import { daysInYear } from "./time";

export const KIND_NAMES = ["interstate", "civil", "colonial", "rebellion", "nonstate", "onesided", "other"] as const;
export type KindName = (typeof KIND_NAMES)[number];
export const CTYPES = ["war", "sub", "umbrella", "context", "ucdp"] as const;
export const CT_WAR = 0, CT_SUB = 1, CT_UMBRELLA = 2, CT_CONTEXT = 3, CT_UCDP = 4;

export interface Chunk { t0: number; t1: number; file: string; n: number; bytes: number }
export interface GedChunk { year: number; file: string; n: number; bytes: number }
/** Historical borders (one PMTiles tileset) and the dates at which they change. */
export interface BordersIndex { tiles: string; changes: number[]; colours: number }
export interface SourceInfo {
  key: string; title: string; publisher: string; version: string; license: string; url: string; citation: string;
  coverage: string;
}
export interface Meta {
  version: string; built_at: string; now: number; now_label: string; range: [number, number];
  data_through: { ucdp_final: number; ucdp_provisional: string };
  kinds: string[]; ctypes: string[]; event_kinds: string[]; srcs: string[];
  counts: Record<string, number>;
  chunks: { named: Chunk[]; ged: GedChunk[]; ged_grid: string; areas: Chunk[] };
  borders: BordersIndex; place_changes: number[]; sources: SourceInfo[]; replays?: string;
  ongoing_revision: { revid: number; timestamp: string; url: string } | null;
}

/** A curated replay's chapter: time, date label, caption, camera [lon, lat, zoom], the battle it is about, source. */
export interface ReplayChapter { t: number; d: string; x: string; v: [number, number, number] | null; r: string | null; u: string }
/** A curated war replay (replays.json): the war, the span played, its pacing profile and chapters. */
export interface ReplayDef {
  id: string; i: number; qid: string; title: string; blurb: string; lt: number; t0: number; t1: number;
  s: number | null; v: [number, number, number] | null; also: number[]; pace: number[] | null; ch: ReplayChapter[];
}

export interface Conflicts {
  n: number;
  id: string[]; name: string[];
  kind: Uint8Array; ct: Uint8Array; src: Uint8Array;
  t0: Float64Array; t1: Float64Array; og: Uint8Array; sig: Float32Array; tier: Uint8Array;
  p: Int32Array; g: Int32Array; r: Int32Array;
  lon: Float32Array; lat: Float32Array; hasPoint: Uint8Array;
  bb: (number[] | null)[]; d: Float64Array; ne: Uint32Array;
  runs: (number[][] | null)[]; yd: (number[] | null)[]; cur: Uint8Array; bt: Uint8Array; eu: Uint8Array;
  byId: Map<string, number>;
}

export interface NamedEvents {
  n: number; id: string[]; name: string[]; k: Uint8Array; t0: Float64Array; t1: Float64Array;
  pr: Uint8Array; jl: Uint8Array; lon: Float32Array; lat: Float32Array; d: Float64Array; c: Int32Array;
  s: Uint16Array; u: string[] | null; // u: source url of curated events ("" for Wikidata ones)
}

export interface GedYear {
  year: number; n: number; t0: Float64Array; t1: Float64Array; lon: Float32Array; lat: Float32Array;
  b: Float64Array; ty: Uint8Array; c: Int32Array; p: Uint32Array; dy: Uint32Array; gp: Uint8Array;
  pv: Uint8Array | null; places: string[]; dyads: string[];
}

export interface GedGrid { n: number; yr: Int16Array; lon: Float32Array; lat: Float32Array; b: Float64Array; cnt: Uint32Array; ty: Uint8Array }

export interface Areas {
  n: number; c: Int32Array; t0: Float64Array; t1: Float64Array; fc: Uint8Array;
  polygons: { i: number; ring: Float32Array }[]; // one entry per ring, i = index into the arrays above
}

export interface Stats {
  y0: number; y1: number; active: number[]; wars: number[]; events: number[]; deaths: (number | null)[];
  deaths_src: { from: number; to: number; src: string }[];
}

export interface BriefConflict { i: number; id: string; name: string; t0: number; t1: number; og: number; kind: string; ct: string; d: number }
export interface DetailEvent { id: string; name: string; k: number; t0: number; t1: number; date: string | null; x: number | null; y: number | null; d: number; s: number; u?: string }
export interface Detail {
  id: string; i: number; name: string; name_lt: string | null; aliases: string[]; desc: string | null;
  kind: string | null; ctype: string; src: string; start: DatePoint | null; end: DatePoint | null; dates: string | null;
  t0: number; t1: number; ongoing: boolean; end_unknown: boolean; dates_derived: boolean;
  parent: BriefConflict | null; group: BriefConflict | null; children: BriefConflict[]; n_children: number;
  members: BriefConflict[]; n_members: number; sides: string[][] | null; participants: string[]; countries: string[];
  deaths: Record<string, number | null>; years: Record<string, number> | null; extract: string | null;
  extract_title: string | null; wikidata: string | null; enwiki: string | null; ltwiki: string | null;
  current: { tier: string; tier_rank: number; start_year: number | null; fatalities: Record<string, { text: string; min: number | null }> | null; label: string; main: boolean } | null;
  events: DetailEvent[]; n_events: number; n_located: number; sig: number; tier: number; sitelinks: number;
  sources: { src: string; id: string; url: string }[]; provisional: boolean;
  /** pacing profile and extent of the war with its theatres and (for umbrellas) member wars */
  replay?: { pace: number[] | null; bb: number[] | null };
}

export interface SearchIndex { n: string[]; a: string[]; k: string[]; i: (number | string)[]; t: number[]; s: number[] }

// --- fetching -----------------------------------------------------------------------------------

let version = "";
const cache = new Map<string, Promise<unknown>>();

export function dataUrl(rel: string): string {
  return `/data/${rel}${version ? `?v=${version}` : ""}`;
}

async function getJson<T>(rel: string): Promise<T> {
  const url = dataUrl(rel);
  let p = cache.get(url) as Promise<T> | undefined;
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText} for ${rel}`);
      return r.json() as Promise<T>;
    });
    p.catch(() => cache.delete(url));
    cache.set(url, p);
  }
  return p;
}

export async function loadMeta(): Promise<Meta> {
  const r = await fetch("/data/meta.json", { cache: "no-cache" });
  if (!r.ok) throw new Error(`meta.json: ${r.status}`);
  const meta = (await r.json()) as Meta;
  version = meta.version;
  return meta;
}

const f64 = (a: (number | null)[]) => Float64Array.from(a, (x) => (x == null ? NaN : x));

export async function loadConflicts(): Promise<Conflicts> {
  const j = await getJson<Record<string, unknown[]> & { n: number }>("conflicts.json");
  const n = j.n;
  const lon = new Float32Array(n), lat = new Float32Array(n), hasPoint = new Uint8Array(n);
  (j.lon as (number | null)[]).forEach((x, i) => {
    const y = (j.lat as (number | null)[])[i];
    if (x != null && y != null) { lon[i] = x; lat[i] = y; hasPoint[i] = 1; }
  });
  const id = j.id as string[];
  return {
    n, id, name: j.name as string[],
    kind: Uint8Array.from(j.kind as number[]), ct: Uint8Array.from(j.ct as number[]), src: Uint8Array.from(j.src as number[]),
    t0: f64(j.t0 as number[]), t1: f64(j.t1 as number[]), og: Uint8Array.from(j.og as number[]),
    sig: Float32Array.from(j.sig as number[]), tier: Uint8Array.from(j.tier as number[]),
    p: Int32Array.from(j.p as number[]), g: Int32Array.from(j.g as number[]), r: Int32Array.from(j.r as number[]),
    lon, lat, hasPoint, bb: j.bb as (number[] | null)[], d: f64(j.d as number[]), ne: Uint32Array.from(j.ne as number[]),
    runs: j.runs as (number[][] | null)[], yd: j.yd as (number[] | null)[], cur: Uint8Array.from(j.cur as number[]),
    bt: Uint8Array.from(j.bt as number[]), eu: Uint8Array.from(j.eu as number[]),
    byId: new Map(id.map((x, i) => [x, i])),
  };
}

export async function loadNamed(chunk: Chunk): Promise<NamedEvents> {
  const j = await getJson<Record<string, unknown[]> & { n: number }>(chunk.file);
  return {
    n: j.n, id: j.id as string[], name: j.name as string[], k: Uint8Array.from(j.k as number[]),
    t0: f64(j.t0 as number[]), t1: f64(j.t1 as number[]), pr: Uint8Array.from(j.pr as number[]),
    jl: Uint8Array.from(j.jl as number[]), lon: Float32Array.from(j.x as number[], (x) => x / 1e4),
    lat: Float32Array.from(j.y as number[], (x) => x / 1e4), d: f64(j.d as number[]), c: Int32Array.from(j.c as number[]),
    s: Uint16Array.from(j.s as number[]), u: (j.u as string[] | undefined) ?? null,
  };
}

export async function loadGedYear(chunk: GedChunk): Promise<GedYear> {
  const j = await getJson<Record<string, unknown[]> & { n: number; year: number }>(chunk.file);
  const n = j.n, year = j.year, days = daysInYear(year);
  const d0 = j.d0 as number[], dd = j.dd as number[];
  const t0 = new Float64Array(n), t1 = new Float64Array(n);
  for (let i = 0; i < n; i++) { t0[i] = year + d0[i] / days; t1[i] = t0[i] + (dd[i] + 1) / days; }
  return {
    year, n, t0, t1, lon: Float32Array.from(j.x as number[], (x) => x / 1e4), lat: Float32Array.from(j.y as number[], (x) => x / 1e4),
    b: f64(j.b as number[]), ty: Uint8Array.from(j.ty as number[]), c: Int32Array.from(j.c as number[]),
    p: Uint32Array.from(j.p as number[]), dy: Uint32Array.from(j.dy as number[]), gp: Uint8Array.from(j.gp as number[]),
    pv: j.pv ? Uint8Array.from(j.pv as number[]) : null, places: j.places as unknown as string[], dyads: j.dyads as unknown as string[],
  };
}

export async function loadGedGrid(file: string): Promise<GedGrid> {
  const j = await getJson<Record<string, number[]>>(file);
  return {
    n: j.yr.length, yr: Int16Array.from(j.yr), lon: Float32Array.from(j.x, (x) => x / 100), lat: Float32Array.from(j.y, (x) => x / 100),
    b: f64(j.b), cnt: Uint32Array.from(j.n), ty: Uint8Array.from(j.ty),
  };
}

export async function loadAreas(chunk: Chunk): Promise<Areas> {
  const j = await getJson<{ n: number; c: number[]; t0: number[]; t1: number[]; fc: number[]; r: number[][][] }>(chunk.file);
  const polygons: Areas["polygons"] = [];
  j.r.forEach((rings, i) => {
    for (const r of rings) polygons.push({ i, ring: Float32Array.from(r, (x) => x / 1000) });
  });
  return { n: j.n, c: Int32Array.from(j.c), t0: f64(j.t0), t1: f64(j.t1), fc: Uint8Array.from(j.fc), polygons };
}

export const loadStats = () => getJson<Stats>("stats.json");
export const loadDetail = (id: string) => getJson<Detail>(`c/${encodeURIComponent(id)}.json`);
export const loadSearch = () => getJson<SearchIndex>("search.json");
export const loadReplays = (file = "replays.json") => getJson<{ n: number; replays: ReplayDef[] }>(file);
